import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// In-memory token storage (secure — not persisted, not accessible to XSS)
let _adminToken = null;
export const setAdminToken = (token) => { _adminToken = token; };
export const getAdminToken = () => _adminToken;
export const clearAdminToken = () => { _adminToken = null; };

// Admin API instance with Bearer token auth (no cookie dependency)
const adminApi = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach Bearer token to every admin request
adminApi.interceptors.request.use((config) => {
  if (_adminToken) {
    config.headers.Authorization = `Bearer ${_adminToken}`;
  }
  return config;
});

// Company API
export const companyApi = {
  get: () => api.get('/company'),
  update: (data) => api.put('/company', data),
};

// Brands API
export const brandsApi = {
  getAll: () => api.get('/brands'),
  getAllAdmin: () => api.get('/admin/brands'),
  get: (id) => api.get(`/brands/${id}`),
  create: (data) => api.post('/brands', data),
  update: (id, data) => api.put(`/brands/${id}`, data),
  delete: (id) => api.delete(`/brands/${id}`),
  updateOrder: (orderUpdates) => api.put('/brands-order', orderUpdates),
  toggleVisibility: (id) => api.put(`/brands/${id}/toggle-visibility`),
};

// Brand News API
export const brandNewsApi = {
  getAll: (brandId) => api.get(`/brands/${brandId}/news`),
  create: (brandId, data) => api.post(`/brands/${brandId}/news`, data),
  update: (brandId, newsId, data) => api.put(`/brands/${brandId}/news/${newsId}`, data),
  delete: (brandId, newsId) => api.delete(`/brands/${brandId}/news/${newsId}`),
};

// Brand Products API
export const brandProductsApi = {
  getAll: (brandId) => api.get(`/brands/${brandId}/products`),
  create: (brandId, data) => api.post(`/brands/${brandId}/products`, data),
  update: (brandId, productId, data) => api.put(`/brands/${brandId}/products/${productId}`, data),
  delete: (brandId, productId) => api.delete(`/brands/${brandId}/products/${productId}`),
};

// Brand Projects API
export const brandProjectsApi = {
  getAll: (brandId) => api.get(`/brands/${brandId}/projects`),
  create: (brandId, data) => api.post(`/brands/${brandId}/projects`, data),
  update: (brandId, projectId, data) => api.put(`/brands/${brandId}/projects/${projectId}`, data),
  delete: (brandId, projectId) => api.delete(`/brands/${brandId}/projects/${projectId}`),
};

// Mesh News API
export const meshNewsApi = {
  getAll: () => api.get('/news'),
  get: (id) => api.get(`/news/${id}`),
  getByBrand: (brandId) => api.get(`/news/by-brand/${brandId}`),
  create: (data) => api.post('/news', data),
  update: (id, data) => api.put(`/news/${id}`, data),
  delete: (id) => api.delete(`/news/${id}`),
};

// Landing Page API
export const landingPageApi = {
  get: () => api.get('/landing-page'),
  update: (data) => api.put('/landing-page', data),
};

// About Page API
export const aboutPageApi = {
  get: () => api.get('/about-page'),
  update: (data) => api.put('/about-page', data),
};

// Admin Auth API (uses adminApi for cookie + Bearer token auth)
export const authApi = {
  login: async (password) => {
    const res = await adminApi.post('/admin/login', { password });
    if (res.data.token) {
      setAdminToken(res.data.token);
    }
    return res;
  },
  verifyToken: () => adminApi.post('/admin/verify-token'),
  logout: async () => {
    const res = await adminApi.post('/admin/logout');
    clearAdminToken();
    return res;
  },
  changePassword: (currentPassword, newPassword) => 
    adminApi.post('/admin/change-password', { 
      current_password: currentPassword, 
      new_password: newPassword 
    }),
};

// Contact Form API
export const contactApi = {
  submit: (data) => api.post('/contact', data),
  getSubmissions: () => api.get('/contact-submissions'),
  markRead: (id, read) => api.put(`/contact-submissions/${id}/read`, { read }),
  delete: (id) => api.delete(`/contact-submissions/${id}`),
};

// Sectors API
export const sectorsApi = {
  getAll: () => api.get('/sectors'),
  get: (id) => api.get(`/sectors/${id}`),
  update: (id, data) => api.put(`/sectors/${id}`, data),
  delete: (id) => api.delete(`/sectors/${id}`),
  updateOrder: (orderUpdates) => api.put('/sectors-order', orderUpdates),
};

// File Upload API
export const uploadApi = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadMultiple: (files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post('/upload/multiple', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  delete: (filename) => api.delete(`/upload/${filename}`),
  list: () => api.get('/uploads/list'),
};

// Catalogue Products API
export const catalogueApi = {
  getAll: (brandId, params = {}) => api.get(`/brands/${brandId}/catalogue`, { params }),
  get: (brandId, slug) => api.get(`/brands/${brandId}/catalogue/${slug}`),
  create: (brandId, data) => api.post(`/brands/${brandId}/catalogue`, data),
  update: (brandId, productId, data) => api.put(`/brands/${brandId}/catalogue/${productId}`, data),
  delete: (brandId, productId) => api.delete(`/brands/${brandId}/catalogue/${productId}`),
  togglePin: (brandId, productId) => api.put(`/brands/${brandId}/catalogue/${productId}/pin`),
  overview: (brandId) => api.get(`/brands/${brandId}/catalogue-overview`),
  families: (brandId) => api.get(`/brands/${brandId}/families`),
  family: (brandId, slug) => api.get(`/brands/${brandId}/families/${slug}`),
};

// Analytics API
export const analyticsApi = {
  trackPageView: (data) => api.post('/analytics/pageview', data),
  getStats: () => api.get('/analytics/stats'),
};

export default api;
